<template>
    <div v-loading="tasksForCalendarLoading"
         class="task-calendar f h-full border-top-1 border-gray-200 hidden-y">
        <div class="h-full pdown-1 border-box overflow-auto with-custom-scrollbar-dark">
            <FullCalendar ref="taskCalendar"
                          class="task-calendar__calendar h-full"
                          :options="calendarOptions">
                <template #eventContent="{ event }">
                    <div class="f-col hidden-x">
                        <span class="font-medium truncate">{{ event.title }}</span>
                        <span class="event-description truncate _mt-0.25">{{ getName(getInitiatorUser(event.extendedProps.task)) }}</span>
                        <span class="event-description truncate _mt-0.25">{{ getOrganizationName(getInitiatorUser(event.extendedProps.task)) }}</span>
                    </div>
                </template>
            </FullCalendar>
        </div>
        <div class="f-col h-full w-20 no-shrink border-left-1 border-gray-200">
            <div class="h-3 f-y-center _px-0.75 no-shrink">
                <our-label :value="selectedDateLabel"
                           color="el-dark"
                           primary />
            </div>
            <el-divider class="_mb-0 _mt-0" />
            <div v-if="selectedDateTasks.length"
                 class="f-col h-full hidden-y">
                <div v-for="(task) in selectedDateTasks"
                     :key="task.id"
                     :class="classes.taskItem(task)">
                    <div class="_p-0.75 hover:bg-accent-0.1 cursor-pointer f-y-center"
                         @click="handleClickTask(task)">
                        <i class="el-icon-s-claim fs-1 text-el-dark _mr-0.75 no-shrink" />
                        <our-label :value="task.data?.comment_initiator || task.name"
                                   color="el-dark font-medium"
                                   class="w-full" />
                        <icon name="link"
                              class="wh-1 no-shrink stroke-el-dark hover:stroke-accent"
                              @click.stop="goToTask(task)" />
                        <i :class="classes.taskItemIcon(task)" />
                    </div>
                    <div v-if="selectedTask.id === task.id"
                         v-loading="task.loading || loading"
                         class="h-full _p-0.75 overflow-auto with-custom-scrollbar-dark">
                        <task-description v-if="task.task"
                                          :task="task.task"
                                          :field-to-display="fieldToDisplay(task.type)"
                                          direction-column />
                        <task-actions v-if="task.task"
                                      :task="task.task"
                                      :actions="actionsWithMood"
                                      :errors="errors"
                                      :editable-fields="editableFieldsWithValidation"
                                      @do-with-data="act" />
                    </div>
                </div>
            </div>
            <div v-else
                 class="text-color--info text-size--body text-align--center _mt-1">
                Задачи отсутствуют
            </div>
        </div>
    </div>
</template>

<script>
import '@fullcalendar/core/vdom'

import { mapActions, mapGetters, mapMutations } from 'vuex'
import { endOfMonth, endOfWeek, isSameDay, startOfMonth, startOfWeek } from 'date-fns'
import locale from 'date-fns/locale/ru'

import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import ruLocales from '@fullcalendar/core/locales/ru'

import { formatAsDate, formatAsLabel } from '@/utils/date'
import { colors } from '@/utils/colors'
import { TASK_ACTIONS } from '@/utils/tasks'
import { then } from '@/utils/immutable'
import { openNew } from '@/utils/browser'

import { resourceable } from '@/store/connectors'

import { types, translateType, getInitiatorUser } from '@/models/tasks'
import { getName, getOrganizationName } from '@/models/users'

import FullCalendar from '@fullcalendar/vue'
import TaskDescription from '@/components/task/TaskDescription'
import TaskActions from '@/components/tasks/TaskActions'

const colorsByType = {
  // [types.PHOTO_ORDER]: colors['purple-pavel-1'],
  // [types.BUILDING_ORDER]: colors['green-pavel-1'],
  // [types.DEFECTS_AND_VIOLATIONS]: colors['red-pavel-1'],
  [types.ACCEPTANCE_OF_WORK]: ({ selected }) => ({
    bg: selected ? colors['blue-pavel-2'] : colors['blue-pavel-3'],
    border: selected ? colors['blue-pavel-2'] : colors['blue-pavel-3'],
    text: selected ? colors['white'] : colors['blue-pavel-2']
  })
}

const labels = {
}

export default {
  name: 'TaskCalendar',
  components: {
    FullCalendar,
    TaskDescription,
    TaskActions
  },
  mixins: [
    resourceable({ on: 'account', name: 'tasksForCalendar' }),
    resourceable({ on: 'tasks', name: 'allowedActions' })
  ],
  props: {
    currentDate: { type: Date, default: () => new Date() },
    calendarView: { type: String, default: 'dayGridWeek' }
  },
  data() {
    return {
      labels,
      
      // NOT REVIEWED
      loading: false,

      taskList: [],
      taskListTotal: 0,
      filter: {},
      page: {
        number: 1,
        size: 500
      },
      selectedDate: new Date(),
      selectedTask: {},

      errors: {},

      getInitiatorUser,
      getName,
      getOrganizationName
    };
  },
  computed: {
    ...mapGetters('tasks', ['editableFieldsWithValidation']),

    classes() {
      return {
        taskItem: (task) => ({
          'f-col hidden-y min-h-2.5': true,
          'h-full': this.selectedTask.id === task.id,
          'no-shrink': this.selectedTask.id !== task.id
        }),
        taskItemIcon: (task) => ({
          'fs-1 text-el-dark _ml-0.75 no-shrink': true,
          'el-icon-minus': this.selectedTask.id === task.id,
          'el-icon-plus': this.selectedTask.id !== task.id
        })
      }
    },

    calendarOptions() {
      return {
        selectable: true,
        unselectAuto: false,
        height: 'auto',
        locales: [ruLocales],
        locale: 'ru',
        plugins: [dayGridPlugin, interactionPlugin],
        initialView: 'dayGridWeek',
        dayMaxEventRows: true,
        views: { dayGridMonth: { dayMaxEventRows: 2 }, dayGridWeek: { dayMaxEventRows: 5 } },
        headerToolbar: false,
        events: this.events,
        dateClick: this.clickDate,
        eventClick: this.clickEvent
      }
    },

    events() {
      return this.taskList
        .filter(({ type }) => colorsByType[type])
        .map(task => {
          const { type } = task

          const selected = this.selectedTask.id
            ? this.selectedTask.id === task.id
            : formatAsDate(task.calendar_at) === formatAsDate(this.selectedDate)
          const colors = colorsByType[type]({ selected })

          const classNames = [
            'cursor-pointer _py-0.25 _px-0.5 el-icon-s-claim fs-0.75',
            this.calendarViewMonth ? 'without-description f-y-center' : 'f-y-start',
            selected ? 'text-white' : 'text-accent',
            task.unread_changes_exists && 'task-badge-unread',
            task.status === 'finished' && 'calendar__task-finished'
          ].filter(is)

          return {
            id: task.id,
            title: task.data?.comment_initiator || task.name,
            date: formatAsDate(task.calendar_at),
            extendedProps: {
              taskId: task.id,
              type: task.type,

              task
            },
            backgroundColor: colors.bg,
            borderColor: colors.border,
            textColor: colors.text,
            classNames: classNames
          };
        })
    },

    legendItems() {
      return [
        types.DEFECTS_AND_VIOLATIONS,
        types.BUILDING_ORDER,
        types.ACCEPTANCE_OF_WORK,
        types.PHOTO_ORDER
      ].map(x => ({
        type: x,
        label: translateType(x, { capital: true }),
        color: colorsByType[x]
      }))
    },

    selectedDateLabel() {
      return this.selectedDate && formatAsLabel(this.selectedDate, { zoned: false, withAlias: false, withTime: false })
    },

    selectedDateTasks() {
      return this.taskList
        .filter(({ calendar_at }) => isSameDay(calendar_at, this.selectedDate))
    },

    calendarViewMonth() {
      return this.calendarView === 'dayGridMonth'
    },

    actionsWithMood() {
      const to = ({ name, translated_name, warnings, messages, allowed_fields, allowed_fields_with_validation }) => {
        const [mood = 'primary', label = translated_name] = ({
          [TASK_ACTIONS.CANCEL]: ['danger'],
          [TASK_ACTIONS.TAKE_TO_WORK]: ['success', 'Согласовать время'],
          [TASK_ACTIONS.TAKE_TO_WORK_SECOND_STAGE]: ['success', 'Взять в работу'],
          [TASK_ACTIONS.FINISH]: ['success', 'Работы приняты'],
          [TASK_ACTIONS.FINISH_SECOND_STAGE]: ['success', 'Работы приняты'],
          [TASK_ACTIONS.FINISH_NOT_ACCEPT]: ['danger', 'Работы не приняты'],
          [TASK_ACTIONS.FINISH_NOT_ACCEPT_SECOND_STAGE]: ['danger', 'Работы не приняты'],
          [TASK_ACTIONS.REFUSE]: ['danger'],
          [TASK_ACTIONS.CHECK_PROJECT_WORK_PRODUCTION]: ['success'],
          [TASK_ACTIONS.NON_CHECK_PROJECT_WORK_PRODUCTION]: ['danger'],
          [TASK_ACTIONS.COMPLETE_REWORK]: ['success']
        })[name] || []

        return {
          name,
          translated_name: label,
          mood,
          warnings,
          messages,
          allowed_fields,
          allowed_fields_with_validation
        }
      }

      return this.allowedActions.map(to)
    }
  },
  watch: {
    currentDate() {
      this.fetch()
    },
    calendarView(view) {
      this.$refs.taskCalendar.getApi().changeView(view, this.currentDate)
      this.fetch()
    }
  },
  mounted() {
    this.sentUserActivity({
      slug: 'task_calendar',
      type: 'opened',
      subject_id: this.$route.params.projectId
    })

    this.fetch()
  },
  methods: {
    ...mapActions('tasks', ['showTask', 'getTasks', 'storeTask', 'makeTaskAction']),
    ...mapActions('activity', ['sentUserActivity']),
    ...mapMutations('dialog', {showDialog: 'SHOW_DIALOG'}),

    fetch() {
      this.fetchTasksForCalendar({
        from: this.calendarViewMonth ? startOfMonth(this.currentDate) : startOfWeek(this.currentDate, { locale }),
        to: this.calendarViewMonth ? endOfMonth(this.currentDate) : endOfWeek(this.currentDate, { locale }),
        withMembers: true
      }).then(tasks => {
        this.taskList = tasks.map(x => ({ ...x, opened: false, loading: true }))
      })
    },

    fetchTask(task) {
      const { type } = task || {}

      return this.showTask({
        taskId: task.id,
        payload: {
          params: {
            include: [
              'project',
              'creator',
              'children',
              'members.user.organization',
              'memberInitiator.user.organization',
              'memberInspectors.user.organization',
              'memberWorkers.user.organization',
              'defectGeneralContractor.organization',
              'acceptanceOfWorkContractor.organization',
              'objectsBuildingOrderConnection.object',
              'objectsAcceptanceOfWorkConnection.object',
              'subjectsBuildingOrderConnection.subject',
              'subjectsAcceptanceOfWorkConnection.subject',
              'linkedDefectProjectObjects'
            ],
            append: [
              'checking_act_template_with_values', 
              'files_count',
              type === types.DEFECTS_AND_VIOLATIONS && 'has_belongs_to_task_rejection_with_type_reject_acceptance_by_defect_with_ast_work_as_task_reason_defect',
              type === types.ACCEPTANCE_OF_WORK && 'has_belongs_to_task_rejection_with_type_reject_acceptance_by_defect_with_ast_work_through_ast_works',
              type === types.ACCEPTANCE_OF_WORK && 'last_message_with_creator',
              type === types.ACCEPTANCE_OF_WORK && 'messages_count_all',
              type === types.ACCEPTANCE_OF_WORK && 'ast_acts_ks2_errors_exists'
            ].filter(is)
          }
        }
      })
    },

    clickDate({ date } = {}) {
      this.selectedDate = date
      this.selectedTask = {}
    },

    clickEvent(info) {
      const { task } = info.event.extendedProps

      this.clickDate({ date: info.event.start })
      this.handleClickTask(task)
      // this.clickTask(task)
    },

    clickTask(task) {
      this.$emit('task-click', task)
    },

    getTaskDataForCalendar(task, forced) {
      task && (!task.task || forced) && this.fetchTask(task)
        .then(({ data }) => {
          this.selectedTask = { ...this.selectedTask, task: data }
          this.taskList = this.taskList.map(x => x.id === task.id ? { ...x, loading: false, task: data } : x)
        })
        .catch(e => {
          console.error(e)
        })
        .finally(() => this.taskList = this.taskList.map(x => x.id === task.id ? { ...x, loading: false } : x))
    },

    handleClickTask(task) {
      if (this.selectedTask.id === task.id) {
        this.selectedTask = {}
      } else {
        this.selectedTask = task
        this.getTaskDataForCalendar(task)
      }
    },

    fieldToDisplay(type) {
      return [
        'type', 'createdAt', 'updatedAt', 'status', 'initiator',

        ...type === types.DEFECTS_AND_VIOLATIONS && [
          'kindDefect', 'expiredAt', 'worker', 'generalContractor'
        ] || [],
        ...type === types.BUILDING_ORDER && [
          'expiredAt', 'worker', 'manager', 'inspector'
        ] || [],
        ...type === types.PHOTO_ORDER && [
          'expiredAt', 'worker', 'commentWorker'
        ] || [],
        ...type === types.ACCEPTANCE_OF_WORK && [
          'startAt', 'engineer', 'contractor', 'commentInitiator', 'axis', 'objects', 'jobTypes', 'commentWorker', 'commentWorkerSecondStage', 'commentContractor', 'documents'
        ] || [],
        ...type === types.CHECKING_ACT && [
          'expiredAt'
        ] || []
      ]
    },

    act(action) {
      const { name, allowed_fields } = action

      const go = data => this.makeTaskAction({
        taskId: this.selectedTask.task.id,
        taskType: this.selectedTask.task.type,
        payload: {
          action: name,
          data
        }
      })
        .then(() => {
          this.getTaskDataForCalendar(this.selectedTask, true)
        })
        .catch(({ messages }) => {
          this.errors = {
            ...messages,
            ...then(messages.action, x => ({ [name]: x }))
          }
          throw { messages }
        })

      const f = allowed_fields.length 
        ? () => this.showDialog({
          dialogName: 'task-acceptance-of-work-dialog',
          payload: { action, task: this.selectedTask.task, withGoToTasks: false },
          action: go
        })
        : () => {
          this.loading = true

          go(this.selectedTask.task)
            .then(() => {
              this.errors = {}
            })
            .finally(() => this.loading = false)
        }

      f()
    },

    goToTask(task) {
      const { id, type } = task || {}

      let r

      r ||= type === types.APPROVAL_PROCESS && this.$router.resolve({ name: 'project.approval.info', params: { processId: id } })
      r ||= this.$router.resolve({ name: 'project.task.info', params: { taskId: id } })

      const { href } = r || {}

      href && openNew(href)
    }
  }
}
</script>
<style lang="scss">
.task-calendar {
  &__calendar {
    .without-description {
      .event-description {
        display: none;
      }
    }
  }
}

.calendar-legend__task-type {
  width: 8px;
  height: 8px;
  border-radius: 8px;
  display: inline-block;
}

.calendar__task-finished {
  opacity: .5;

  .fc-event-title {
    text-decoration: line-through;
  }
}

.task-badge-unread {
  position: relative;

  &:before {
    content: "";
    z-index: 9;
    position: absolute;
    left: 4px;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #ffe300;
  }

  &.fc {
    &-h-event {
      .fc-event-title-container {
        margin-left: 16px;
      }
    }
  }
}

.fc {
  &-daygrid,
  table.fc-scrollgrid,
  &-scroller-harness,
  &-scroller,
  &-daygrid-body,
  table.fc-scrollgrid-sync-table,
  &-daygrid-day-frame {
    height: 100%;
  }

  &-col-header, &-daygrid-body, &-daygrid-body > .fc-scrollgrid-sync-table {
    width: 100% !important;
  }

  & table.fc-scrollgrid  {
    border: none;

    thead td, thead th {
      border: none;
    }

    tbody td {
      border-width: 1px;
    }
  }

  &-col-header {
    & .fc-col-header-cell {
      border: none;
      text-transform: capitalize;
      
      & .fc-scrollgrid-sync-inner {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 3rem;
      }
    }
  }

  & &-daygrid-day {
    &-frame {
      padding: 0.5rem;
    }

    &-top {
      flex-direction: row;
    }

    &-events {
      min-height: 0 !important;
      margin-bottom: 0 !important;
      
      .fc-daygrid-event {
        &::before {
          font-size: 0.9rem;
        }
      }

      .fc-event-main {
        width: calc(100% - 0.9rem);
        padding-left: 0.25rem;
      }

      .fc-event-title {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }


      .fc-daygrid-more-link {
        color: #409EFF;
      }
    }

    &.fc-day-today {
      background: none;
  
      .fc-daygrid-day-top {
        color: #409EFF;
      }
    }
  }

  &-h-event {
    .fc-event-title-container {
      cursor: pointer !important;
    }
  }

  .fc-daygrid-day-frame {
    cursor: pointer;
  }
}

.collapse-list {
  &__item {
    .el-collapse-item__wrap, .el-collapse-item__header {
      background-color: #F5F5F6;
      padding: 0 20px;
    }
  }
}
</style>
